import * as yup from "yup";
import { decodeMetadataValue, escapeMetadataValue } from "./sanitizeMetadata";

export const INACTIVE_CLASSIFICATION_ERROR =
	"Selected Investor Classification is no longer available. Please select another option.";

export interface IndividualInvestorClassificationItem {
	id: number;
	label: string;
	status: string;
	investorType: string;
	sortOrder: number;
}

export type IndividualInvestorClassificationsMap = Record<
	string,
	IndividualInvestorClassificationItem[]
>;

export interface IInvestorProfileForClassification {
	investorType?: string | null;
	investorClassificationCriteriaId?: number | null;
	investorClassificationCriteriaLabel?: string | null;
	investorClassificationCriteriaStatus?: string | null;
	investorClassificationCriteria?: {
		id: number;
		label: string;
		status: string;
		investorType: string;
	} | null;
	sourceOfWealth?: string | null;
	sourceOfFunds?: string | null;
	incomeLevel?: string | null;
	annualAnticipatedInvestmentAmount?: string | null;
}

export type IndividualClassificationSelectOption = {
	label: string;
	value: number | string;
	isDisabled?: boolean;
};

export interface IProfileCompleteForVerification {
	isComplete?: boolean;
	detailProgress?: {
		amlStatus?: string;
	};
}

/** Matches Wallet / TopUp / Withdrawal "fully verified" gate. */
export function isProfileCompleteWithAmlApproved(
	profileComplete?: IProfileCompleteForVerification | null,
): boolean {
	return Boolean(
		profileComplete?.isComplete && profileComplete?.detailProgress?.amlStatus === "APPROVED",
	);
}

export function isIndividualInvestorUser(permissions: string[] = []): boolean {
	return (
		!permissions.includes("WEB_INVESTOR_CORP_MANAGE") &&
		!permissions.includes("WEB_OMNIBUS_AUTHORIZE") &&
		!permissions.includes("WEB_ISSUER_AUTHORIZE")
	);
}

export function isFullyVerifiedIndividualInvestor(
	profileComplete?: IProfileCompleteForVerification | null,
): boolean {
	return isProfileCompleteWithAmlApproved(profileComplete);
}

export function needsIndividualClassificationUpdate(
	profile?: IInvestorProfileForClassification | null,
): boolean {
	if (!profile) return false;

	const hasMissingFields =
		!profile.investorClassificationCriteriaId ||
		!profile.sourceOfWealth ||
		!profile.sourceOfFunds ||
		!profile.incomeLevel ||
		!profile.annualAnticipatedInvestmentAmount;

	const hasInactiveClassification =
		Boolean(profile.investorClassificationCriteriaId) &&
		profile.investorClassificationCriteriaStatus !== "ACTIVE";

	return hasMissingFields || hasInactiveClassification;
}

export function getActiveIndividualClassificationOptions(
	investorType: string | undefined,
	classifications: IndividualInvestorClassificationsMap = {},
) {
	if (!investorType) return [];

	return (classifications[investorType] ?? [])
		.filter((item) => item.status === "ACTIVE")
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((item) => ({ label: item.label, value: item.id }));
}

export function isActiveIndividualClassificationId(
	id: number | string | null | undefined,
	investorType: string | undefined,
	classifications: IndividualInvestorClassificationsMap = {},
): boolean {
	if (id === null || id === undefined || id === "") return false;
	if (!investorType) return false;

	const normalizedId = Number(id);
	if (Number.isNaN(normalizedId)) return false;

	return getActiveIndividualClassificationOptions(investorType, classifications).some(
		(option) => Number(option.value) === normalizedId,
	);
}

export function getInitialClassificationValue(
	profile?: IInvestorProfileForClassification | null,
): number | null {
	if (!profile?.investorClassificationCriteriaId) return null;
	if (profile.investorClassificationCriteriaStatus !== "ACTIVE") return null;
	return profile.investorClassificationCriteriaId;
}

export function isSelectedInactiveProfileClassification(
	selectedId: number | string | null | undefined,
	profile?: IInvestorProfileForClassification | null,
): boolean {
	if (selectedId === null || selectedId === undefined || selectedId === "") return false;
	if (!profile?.investorClassificationCriteriaId) return false;
	if (profile.investorClassificationCriteriaStatus === "ACTIVE") return false;

	return Number(selectedId) === Number(profile.investorClassificationCriteriaId);
}

/** Profile's inactive classification for the dropdown when metadata no longer includes it. */
export function getInactiveProfileClassificationOption(
	profile?: IInvestorProfileForClassification | null,
	investorType?: string | null,
	classifications: IndividualInvestorClassificationsMap = {},
): { value: number; label: string; isDisabled: true } | null {
	const id = profile?.investorClassificationCriteriaId;
	if (!id) return null;
	if (profile?.investorClassificationCriteriaStatus === "ACTIVE") return null;
	if (!investorType || investorType !== profile.investorType) return null;

	const metadataItem = (classifications[investorType] ?? []).find(
		(item) => Number(item.id) === Number(id),
	);
	const label = metadataItem?.label ?? profile.investorClassificationCriteriaLabel;
	if (!label) return null;

	return { value: id, label, isDisabled: true };
}

/** Active + saved inactive (disabled) options for the individual requirement modal. */
export function getIndividualRequirementClassificationOptions(
	investorType: string | undefined,
	classifications: IndividualInvestorClassificationsMap = {},
	profile?: IInvestorProfileForClassification | null,
): IndividualClassificationSelectOption[] {
	if (!investorType) {
		return [{ value: "", label: "Please select" }];
	}

	const options = getActiveIndividualClassificationOptions(investorType, classifications).map(
		(item) => ({
			...item,
			isDisabled: false,
		}),
	);

	const savedId = profile?.investorClassificationCriteriaId;
	if (
		!savedId ||
		profile?.investorClassificationCriteriaStatus === "ACTIVE" ||
		profile?.investorType !== investorType
	) {
		return [{ value: "", label: "Please select" }, ...options];
	}

	const metadataItem = (classifications[investorType] ?? []).find(
		(item) => Number(item.id) === Number(savedId),
	);
	const savedCriteria = profile?.investorClassificationCriteria;
	const label =
		(savedCriteria && Number(savedCriteria.id) === Number(savedId)
			? savedCriteria.label
			: undefined) ??
		metadataItem?.label ??
		profile.investorClassificationCriteriaLabel;

	if (label) {
		const existingIndex = options.findIndex(
			(option) => Number(option.value) === Number(savedId),
		);

		if (existingIndex >= 0) {
			options[existingIndex] = { ...options[existingIndex], isDisabled: true };
		} else {
			options.push({ value: savedId, label, isDisabled: true });
		}
	}

	return [{ value: "", label: "Please select" }, ...options];
}

export const SOURCE_OF_FUNDS_OTHERS_MAX_LENGTH = 200;

export function isSourceOfFundsOthers(value?: string | null): boolean {
	return value?.toString().trim().toLowerCase() === "others";
}

/** Resolve saved Source of Funds into dropdown + optional free-text "Others" fields. */
export function resolveSourceOfFundsFormValues(
	savedValue: string | null | undefined,
	optionValues: Array<{ value: string | number }> = [],
): { sourceOfFunds: string; sourceOfFundsOthers: string } {
	const decoded = decodeMetadataValue(savedValue) ?? "";
	if (!decoded) {
		return { sourceOfFunds: "", sourceOfFundsOthers: "" };
	}

	if (!optionValues.length) {
		return {
			sourceOfFunds: isSourceOfFundsOthers(decoded) ? "Others" : decoded,
			sourceOfFundsOthers: "",
		};
	}

	const inList = optionValues.some((item) => String(item.value) === decoded);
	if (inList) {
		return { sourceOfFunds: decoded, sourceOfFundsOthers: "" };
	}

	return { sourceOfFunds: "Others", sourceOfFundsOthers: decoded };
}

export function getSourceOfFundsSubmitValue(
	sourceOfFunds: string,
	sourceOfFundsOthers: string | null | undefined,
): string {
	if (isSourceOfFundsOthers(sourceOfFunds)) {
		return (sourceOfFundsOthers ?? "").trim();
	}
	return escapeMetadataValue(sourceOfFunds) as string;
}

export function createSourceOfFundsOthersYupSchema() {
	return yup
		.string()
		.required("This field is required.")
		.max(SOURCE_OF_FUNDS_OTHERS_MAX_LENGTH, "Maximum 200 characters allowed.")
		.test(
			"no-html-or-control-chars",
			"HTML tags and control characters are not allowed.",
			(value) => {
				if (!value) return true;
				if (/<[^>]*>/g.test(value)) return false;
				// eslint-disable-next-line no-control-regex
				if (/[\x00-\x1F\x7F]/g.test(value)) return false;
				return true;
			},
		)
		.test(
			"not-blank",
			"This field is required.",
			(value) => value == null || value.trim().length > 0,
		)
		.nullable();
}
